import {
  boolean,
  index,
  integer,
  numeric,
  pgEnum,
  pgTable,
  smallint,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { applicationsTable } from "./applicationsSchema";
import {
  ageRangeEnum,
  ageRangeEnumSchema,
  americanIndianOrAlaskaNativeStatusEnum,
  americanIndianOrAlaskaNativeStatusEnumSchema,
  blindOrVisionLossStatusEnum,
  blindOrVisionLossStatusEnumSchema,
  breastCancerStatusEnum,
  breastCancerStatusEnumSchema,
  cervicalCancerStatusEnum,
  cervicalCancerStatusEnumSchema,
  childCaregiverStatusEnum,
  childCaregiverStatusEnumSchema,
  citizenshipStatusEnum,
  citizenshipStatusEnumSchema,
  disabledStatusEnum,
  disabledStatusEnumSchema,
  employmentStatusEnum,
  employmentStatusEnumSchema,
  fosterCareFormerlyStatusEnum,
  fosterCareFormerlyStatusEnumSchema,
  healthInsuranceOrCoverageStatusEnumSchema,
  maritalStatusEnum,
  maritalStatusEnumSchema,
  medicalExpenses3MonthsHelpNeededStatusEnum,
  medicalExpenses3MonthsHelpNeededStatusEnumSchema,
  medicarePlanTypeEnum,
  medicarePlanTypeEnumSchema,
  needsHelpDailyActivitiesStatusEnum,
  needsHelpDailyActivitiesStatusEnumSchema,
  payFrequencyEnum,
  payFrequencyEnumSchema,
  pregnantStatusEnum,
  pregnantStatusEnumSchema,
  studentFullTime18to20StatusEnum,
  studentFullTime18to20StatusEnumSchema,
  taxClaimedAsDependentStatusEnum,
  taxClaimedAsDependentStatusEnumSchema,
  taxDependentsClaimedStatusEnum,
  taxDependentsClaimedStatusEnumSchema,
  taxFilingStatusEnum,
  taxFilingStatusEnumSchema,
  tobaccoStatusEnum,
  tobaccoStatusEnumSchema,
  unemploymentCompensationStatusEnum,
  unemploymentCompensationStatusEnumSchema,
  veteranOrMilitaryMemberStatusEnum,
  veteranOrMilitaryMemberStatusEnumSchema,
  type HealthInsuranceOrCoverageStatusEnumType,
} from "./commonSchema";
import { eligibilityCategoriesTable } from "./eligibilityCategoriesSchema";
import { householdMembersTable } from "./householdMembersSchema";
import { customDate, customTimestamp } from "./utils/types";

export const applicationMemberTypeEnumSchema = z.enum(["ADULT", "CHILD"]);

export const applicationMemberTypeEnum = pgEnum(
  "application_member_type_enum",
  applicationMemberTypeEnumSchema.enum,
);

export const applicationMembersTable = pgTable(
  "application_members",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    applicationId: uuid("application_id")
      .references(() => applicationsTable.id, { onDelete: "cascade" })
      .notNull(),
    // Set to null when household member is deleted to preserve application records.
    // Application members are snapshots at time of submission and should persist independently.
    householdMemberId: uuid("household_member_id").references(
      () => householdMembersTable.id,
      { onDelete: "set null" },
    ),
    eligibilityCategoryId: uuid("eligibility_category_id").references(
      () => eligibilityCategoriesTable.id,
      { onDelete: "set null" },
    ),
    memberType: applicationMemberTypeEnum("member_type").notNull(),
    isApplying: boolean("is_applying").notNull(),
    firstName: text("first_name"),
    lastName: text("last_name"),
    dateOfBirth: customDate("date_of_birth"),
    ageRange: ageRangeEnum("age_range"),
    isHomeless: boolean("is_homeless"),
    hasNoMailingAddress: boolean("has_no_mailing_address"),
    mainAddressStreetOne: text("main_address_street_one"),
    mainAddressStreetTwo: text("main_address_street_two"),
    mainAddressComplement: text("main_address_complement"),
    mainAddressCity: text("main_address_city"),
    mainAddressState: text("main_address_state"),
    mainAddressZip: text("main_address_zip"),
    mainCounty: text("main_county"),
    mailingAddressStreetOne: text("mailing_address_street_one"),
    mailingAddressStreetTwo: text("mailing_address_street_two"),
    mailingAddressComplement: text("mailing_address_complement"),
    mailingAddressCity: text("mailing_address_city"),
    mailingAddressState: text("mailing_address_state"),
    mailingAddressZip: text("mailing_address_zip"),
    mailingCounty: text("mailing_county"),
    phoneNumber: text("phone_number"),
    ssn: text("ssn"),
    medicaidSubscriberId: text("medicaid_subscriber_id"),
    medicaidCaseNumber: text("medicaid_case_number"),
    email: text("email"),
    birthCity: text("birth_city"),
    birthState: text("birth_state"),
    birthCountry: text("birth_country"),
    pregnantStatus: pregnantStatusEnum("pregnant_status"),
    pregnantBabiesExpected: smallint("pregnant_babies_expected"),
    pregnantDueDate: customDate("pregnant_due_date"),
    breastCancerStatus: breastCancerStatusEnum("breast_cancer_status"),
    cervicalCancerStatus: cervicalCancerStatusEnum("cervical_cancer_status"),
    disabledStatus: disabledStatusEnum("disabled_status"),
    blindOrVisionLossStatus: blindOrVisionLossStatusEnum(
      "blind_or_vision_loss_status",
    ),
    fosterCareFormerlyStatus: fosterCareFormerlyStatusEnum(
      "foster_care_formerly_status",
    ),
    maritalStatus: maritalStatusEnum("marital_status"),
    childCaregiverStatus: childCaregiverStatusEnum("child_caregiver_status"),
    needsHelpDailyActivitiesStatus: needsHelpDailyActivitiesStatusEnum(
      "needs_help_daily_activities_status",
    ),
    veteranOrMilitaryMemberStatus: veteranOrMilitaryMemberStatusEnum(
      "veteran_or_military_member_status",
    ),
    americanIndianOrAlaskaNativeStatus: americanIndianOrAlaskaNativeStatusEnum(
      "american_indian_or_alaska_native_status",
    ),
    studentFullTime18to20Status: studentFullTime18to20StatusEnum(
      "student_full_time_18_to_20_status",
    ),
    medicalExpenses3MonthsHelpNeededStatus:
      medicalExpenses3MonthsHelpNeededStatusEnum(
        "medical_expenses_3_months_help_needed_status",
      ),
    tobaccoStatus: tobaccoStatusEnum("tobacco_status"),
    citizenshipStatus: citizenshipStatusEnum("citizenship_status"),
    // Stored as text[] rather than health_insurance_or_coverage_status_enum[] so GCP
    // Datastream can replicate it to BigQuery (Datastream cannot map arrays of enum
    // types). Values are still validated against healthInsuranceOrCoverageStatusEnumSchema
    // in the insert/select schemas below. See issue #512.
    healthInsuranceOrCoverageStatus: text("health_insurance_or_coverage_status")
      .array()
      .$type<HealthInsuranceOrCoverageStatusEnumType[]>(),
    healthInsuranceOrCoverageStatusLastCheckedAt: customDate(
      "health_insurance_or_coverage_status_last_checked_at",
    ),
    healthInsuranceOrCoverageOptionsOther: text(
      "health_insurance_or_coverage_options_other",
    ),
    medicarePlanType: medicarePlanTypeEnum("medicare_plan_type"),
    taxFilingStatus: taxFilingStatusEnum("tax_filing_status"),
    taxDependentsClaimedStatus: taxDependentsClaimedStatusEnum(
      "tax_dependents_claimed_status",
    ),
    taxClaimedAsDependentStatus: taxClaimedAsDependentStatusEnum(
      "tax_claimed_as_dependent_status",
    ),
    employmentStatus: employmentStatusEnum("employment_status"),
    employmentStatusSelfEmployedDetails: text(
      "employment_status_self_employed_details",
    ),
    unemploymentCompensationStatus: unemploymentCompensationStatusEnum(
      "unemployment_compensation_status",
    ),
    unemploymentCompensationAmount: integer("unemployment_compensation_amount"),
    workWeekHours: numeric("work_week_hours"),
    payFrequency: payFrequencyEnum("pay_frequency"),
    employerName: text("employer_name"),
    employerPhoneNumber: text("employer_phone_number"),
    employerAddressStreetOne: text("employer_address_street_one"),
    employerAddressStreetTwo: text("employer_address_street_two"),
    employerAddressCity: text("employer_address_city"),
    employerAddressState: text("employer_address_state"),
    employerAddressZip: text("employer_address_zip"),
    // Income fields
    hasIncome: boolean("has_income"),
    grossMonthlyIncome: integer("gross_monthly_income"),
    selfEmploymentNetMonthlyIncome: integer(
      "self_employment_net_monthly_income",
    ),
    alimonyMonthlyIncome: integer("alimony_monthly_income"),
    otherMonthlyIncome: integer("other_monthly_income"),
    pensionMonthlyIncome: integer("pension_monthly_income"),
    propertyRentalMonthlyIncome: integer("property_rental_monthly_income"),
    retirementMonthlyIncome: integer("retirement_monthly_income"),
    ssaMonthlyIncome: integer("ssa_monthly_income"),
    ssdiMonthlyIncome: integer("ssdi_monthly_income"),
    workersCompensationMonthlyIncome: integer(
      "workers_compensation_monthly_income",
    ),
    // Expense fields
    hasExpense: boolean("has_expense"),
    childcareMonthlyExpense: integer("childcare_monthly_expense"),
    alimonyPaidMonthlyExpense: integer("alimony_paid_monthly_expense"),
    iraContributionsMonthlyExpense: integer(
      "ira_contributions_monthly_expense",
    ),
    movingMonthlyExpense: integer("moving_monthly_expense"),
    otherMonthlyExpense: integer("other_monthly_expense"),
    studentLoansMonthlyExpense: integer("student_loans_monthly_expense"),
    tuitionFeesMonthlyExpense: integer("tuition_fees_monthly_expense"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("application_members_application_id_index").on(table.applicationId),
    index("application_members_household_member_id_index").on(
      table.householdMemberId,
    ),
    unique("application_members_application_household_member_unique").on(
      table.applicationId,
      table.householdMemberId,
    ),
  ],
);

export const applicationMemberInsertSchema = createInsertSchema(
  applicationMembersTable,
  {
    applicationId: z.uuid(),
    householdMemberId: z.uuid().nullish(),
    eligibilityCategoryId: z.uuid().nullish(),
    memberType: applicationMemberTypeEnumSchema,
    dateOfBirth: z.iso.date().nullish(),
    ageRange: ageRangeEnumSchema.nullish(),
    pregnantStatus: pregnantStatusEnumSchema.nullish(),
    breastCancerStatus: breastCancerStatusEnumSchema.nullish(),
    cervicalCancerStatus: cervicalCancerStatusEnumSchema.nullish(),
    disabledStatus: disabledStatusEnumSchema.nullish(),
    blindOrVisionLossStatus: blindOrVisionLossStatusEnumSchema.nullish(),
    fosterCareFormerlyStatus: fosterCareFormerlyStatusEnumSchema.nullish(),
    maritalStatus: maritalStatusEnumSchema.nullish(),
    childCaregiverStatus: childCaregiverStatusEnumSchema.nullish(),
    needsHelpDailyActivitiesStatus:
      needsHelpDailyActivitiesStatusEnumSchema.nullish(),
    veteranOrMilitaryMemberStatus:
      veteranOrMilitaryMemberStatusEnumSchema.nullish(),
    americanIndianOrAlaskaNativeStatus:
      americanIndianOrAlaskaNativeStatusEnumSchema.nullish(),
    studentFullTime18to20Status:
      studentFullTime18to20StatusEnumSchema.nullish(),
    medicalExpenses3MonthsHelpNeededStatus:
      medicalExpenses3MonthsHelpNeededStatusEnumSchema.nullish(),
    tobaccoStatus: tobaccoStatusEnumSchema.nullish(),
    citizenshipStatus: citizenshipStatusEnumSchema.nullish(),
    healthInsuranceOrCoverageStatus: z
      .array(healthInsuranceOrCoverageStatusEnumSchema)
      .nullish(),
    medicarePlanType: medicarePlanTypeEnumSchema.nullish(),
    taxFilingStatus: taxFilingStatusEnumSchema.nullish(),
    taxDependentsClaimedStatus: taxDependentsClaimedStatusEnumSchema.nullish(),
    taxClaimedAsDependentStatus:
      taxClaimedAsDependentStatusEnumSchema.nullish(),
    employmentStatus: employmentStatusEnumSchema.nullish(),
    unemploymentCompensationStatus:
      unemploymentCompensationStatusEnumSchema.nullish(),
    payFrequency: payFrequencyEnumSchema.nullish(),
  },
).omit({
  createdAt: true,
  updatedAt: true,
});

export type ApplicationMemberInsertType = z.infer<
  typeof applicationMemberInsertSchema
>;

export const applicationMembersSchema = createSelectSchema(
  applicationMembersTable,
  {
    // Column is now text[] (see above), so keep the enum constraint here explicitly.
    healthInsuranceOrCoverageStatus: z
      .array(healthInsuranceOrCoverageStatusEnumSchema)
      .nullable(),
  },
);
export type ApplicationMemberType = z.infer<typeof applicationMembersSchema>;
